import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import withTranslate from 'containers/widgets/withTranslate';
import Button from 'components/atoms/Button';
import ProgressBar from 'components/molecules/ProgressBar';
import PageWrapper from 'components/organisms/PageOutlines/PageWrapper';
import AlertMessage, { LAYOUT_OPTIONS } from 'components/utilities/AlertMessage';
import toaster from 'utils/toaster';
import { convertToCsv, download } from 'utils/export';
import useScrollSpy from 'hooks/useScrollSpy';
import { ALL_QUESTIONS_COUNT, SECTIONS, TOAST_CONFIG } from './constants';
import SectionsSidebar from './components/SectionsSidebar';
import ContextSection from './components/ContextSection';
import SectionSkeleton from './components/SectionSkeleton';
import VersionsShelf from './components/VersionsShelf';
import ConflictDialog from './components/ConflictDialog';
import aiContextService, { CONFLICT_ERROR } from './service';
import { formatVersionDate } from './utils/dateFormat';
import useVersionPreview from './hooks/useVersionPreview';
import useConflictResolution from './hooks/useConflictResolution';
import css from './page.css';

const AI_CONTEXT_PREFIX = 'STATIC.PAGES.AI_CONTEXT';
const TOASTS_PREFIX = `${AI_CONTEXT_PREFIX}.TOASTS`;
const PREVIEW_BANNER_PREFIX = `${AI_CONTEXT_PREFIX}.PREVIEW_BANNER`;

const EXPORT_FILE_BASENAME = 'ai_context';
const EXPORT_FIELD_KEYS = [
    `${AI_CONTEXT_PREFIX}.EXPORT_FIELDS.THEME`,
    `${AI_CONTEXT_PREFIX}.EXPORT_FIELDS.SUBJECT`,
    `${AI_CONTEXT_PREFIX}.EXPORT_FIELDS.QUESTION`,
    `${AI_CONTEXT_PREFIX}.EXPORT_FIELDS.DESCRIPTION`,
    `${AI_CONTEXT_PREFIX}.EXPORT_FIELDS.ANSWER`,
];

// UTF-8 BOM so Excel opens non-ASCII text.
const UTF8_BOM = '\uFEFF';

const AiContext = ({ translate }) => {
    const [answers, setAnswers] = useState({});
    const [savedAnswers, setSavedAnswers] = useState({});
    const [maxLengths, setMaxLengths] = useState({});
    const [loadedLatestVersionId, setLoadedLatestVersionId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [openSections, setOpenSections] = useState(() =>
        SECTIONS.reduce((acc, section) => ({ ...acc, [section.id]: true }), {})
    );
    const [isVersionsShelfOpen, setIsVersionsShelfOpen] = useState(false);
    const [restoringVersionId, setRestoringVersionId] = useState(null);
    const { activeSectionId, setActiveSectionId, setSectionRef, setSentinelRef, scrollToSection } = useScrollSpy({
        sections: SECTIONS,
        enabled: !isLoading,
    });

    const {
        isPreviewing,
        previewingVersionId,
        previewVersionMeta,
        previewAnswers,
        loadingPreviewVersionId,
        previewVersion,
        exitPreview,
    } = useVersionPreview(translate);

    const applySavedVersion = ({ answers: saved, latestVersionId }) => {
        const normalized = saved || {};
        setAnswers(normalized);
        setSavedAnswers(normalized);
        if (latestVersionId != null) setLoadedLatestVersionId(latestVersionId);
    };

    const { conflict, isResolvingConflict, setConflict, cancelConflict, resolveConflict } = useConflictResolution({
        answers,
        setAnswers,
        applySavedVersion,
        translate,
    });

    const visibleAnswers = useMemo(() => (isPreviewing ? previewAnswers || {} : answers), [
        isPreviewing,
        previewAnswers,
        answers,
    ]);

    useEffect(() => {
        let cancelled = false;
        aiContextService
            .getContext()
            .then(loaded => {
                if (cancelled) return;
                setAnswers(loaded.answers);
                setSavedAnswers(loaded.answers);
                setMaxLengths(loaded.maxLengths);
                setLoadedLatestVersionId(loaded.latestVersionId);
            })
            .catch(() => {
                if (!cancelled) {
                    toaster.error(translate(`${TOASTS_PREFIX}.LOAD_FAILED`), TOAST_CONFIG);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [translate]);

    const answeredBySectionId = useMemo(
        () =>
            SECTIONS.reduce((acc, section) => {
                acc[section.id] = section.questions.filter(q => (visibleAnswers[q.id] || '').trim()).length;
                return acc;
            }, {}),
        [visibleAnswers]
    );

    const answeredCount = useMemo(() => Object.values(answeredBySectionId).reduce((sum, count) => sum + count, 0), [
        answeredBySectionId,
    ]);

    const hasUnsavedChanges = useMemo(() => {
        const ids = new Set([...Object.keys(answers), ...Object.keys(savedAnswers)]);
        return Array.from(ids).some(id => (answers[id] || '') !== (savedAnswers[id] || ''));
    }, [answers, savedAnswers]);

    useEffect(() => {
        if (!hasUnsavedChanges) return undefined;
        const handler = e => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges]);

    const handleChange = (id, value) => setAnswers(prev => ({ ...prev, [id]: value }));

    const handleToggleSection = id => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

    const exportFields = EXPORT_FIELD_KEYS.map(fieldKey => translate(fieldKey));

    const handleExport = () => {
        const rows = SECTIONS.flatMap(section =>
            section.questions.map(question => [
                translate(section.name),
                translate(question.title),
                translate(question.description),
                question.placeholder ? translate(question.placeholder) : '',
                visibleAnswers[question.id] || '',
            ])
        );
        const filename =
            isPreviewing && previewVersionMeta?.created
                ? `${EXPORT_FILE_BASENAME}_${moment
                      .utc(previewVersionMeta.created)
                      .local()
                      .format('YYYY-MM-DD_HHmm')}.csv`
                : `${EXPORT_FILE_BASENAME}.csv`;
        download(filename, UTF8_BOM + convertToCsv(exportFields, rows));
    };

    const handleSave = () => {
        if (isSaving || isPreviewing || !hasUnsavedChanges) return;
        setIsSaving(true);
        aiContextService
            .saveAnswers(answers, loadedLatestVersionId)
            .then(result => {
                applySavedVersion(result);
                toaster.success(translate(`${TOASTS_PREFIX}.SAVE_SUCCESS`), TOAST_CONFIG);
            })
            .catch(err => {
                if (err?.type === CONFLICT_ERROR) {
                    setConflict(err);
                    return;
                }
                const message = err?.value || translate(`${TOASTS_PREFIX}.SAVE_FAILED`);
                toaster.error(message, TOAST_CONFIG);
            })
            .finally(() => setIsSaving(false));
    };

    const handleSectionFocus = e => {
        const anchor = e.target.closest('[data-section-id]');
        if (anchor?.dataset.sectionId) {
            setActiveSectionId(anchor.dataset.sectionId);
        }
    };

    const handleSidebarClick = id => {
        setOpenSections(prev => ({ ...prev, [id]: true }));
        scrollToSection(id);
    };

    const handleOpenVersions = () => setIsVersionsShelfOpen(true);
    const handleCloseVersions = () => setIsVersionsShelfOpen(false);

    const handlePreviewVersion = version => previewVersion(version, { onLoaded: () => setIsVersionsShelfOpen(false) });

    const handleRestoreVersion = version => {
        if (restoringVersionId != null) return;
        if (hasUnsavedChanges) {
            // eslint-disable-next-line no-alert
            if (!window.confirm(translate(`${AI_CONTEXT_PREFIX}.UNSAVED_EDITS_WARNING`))) return;
        }
        setRestoringVersionId(version.id);
        aiContextService
            .restoreVersion(version.id, loadedLatestVersionId)
            .then(result => {
                applySavedVersion(result);
                exitPreview();
                setIsVersionsShelfOpen(false);
                toaster.success(translate(`${TOASTS_PREFIX}.RESTORE_SUCCESS`), TOAST_CONFIG);
            })
            .catch(err => {
                if (err?.type === CONFLICT_ERROR) {
                    setConflict(err);
                    return;
                }
                const message = err?.value || translate(`${TOASTS_PREFIX}.RESTORE_FAILED`);
                toaster.error(message, TOAST_CONFIG);
            })
            .finally(() => setRestoringVersionId(null));
    };

    const progressPercentage = (answeredCount / ALL_QUESTIONS_COUNT) * 100;
    const saveDisabled = isSaving || isLoading || isPreviewing || !hasUnsavedChanges;

    return (
        <PageWrapper className={css.page} disablePadding>
            <div className={css.topBar}>
                <div className={css.titleBlock}>
                    <h1 className={css.pageTitle}>{translate(`${AI_CONTEXT_PREFIX}.TITLE`)}</h1>
                    <div className={css.subtitle}>{translate(`${AI_CONTEXT_PREFIX}.SUBTITLE`)}</div>
                </div>
            </div>

            <div className={css.summaryBar}>
                <div className={css.progressGroup}>
                    <span className={css.progressLabel}>{translate(`${AI_CONTEXT_PREFIX}.PROGRESS`)}</span>
                    <div className={css.progressBar}>
                        <ProgressBar
                            progressSections={[{ percentage: progressPercentage }]}
                            barHeight="6px"
                            barWidth="180px"
                            containerColor="var(--gray200)"
                            fillColor="var(--blue300)"
                        />
                    </div>
                    <span className={css.progressCount}>
                        {answeredCount}/{ALL_QUESTIONS_COUNT}
                    </span>
                </div>
                <div className={css.actions}>
                    <Button type="secondary" Icon="history" onClick={handleOpenVersions} disabled={isLoading}>
                        {translate(`${AI_CONTEXT_PREFIX}.VERSIONS`)}
                    </Button>
                    <Button type="secondary" Icon="download" onClick={handleExport}>
                        {translate(`${AI_CONTEXT_PREFIX}.EXPORT`)}
                    </Button>
                    <Button type="primary" onClick={handleSave} showSpinner={isSaving} disabled={saveDisabled}>
                        {translate(`${AI_CONTEXT_PREFIX}.SAVE`)}
                    </Button>
                </div>
            </div>

            {isPreviewing && (
                <div className={css.previewBanner}>
                    <span className={css.previewText}>
                        {translate(`${PREVIEW_BANNER_PREFIX}.TEXT`, {
                            dateLabel: previewVersionMeta?.created
                                ? formatVersionDate(previewVersionMeta.created)
                                : translate(`${PREVIEW_BANNER_PREFIX}.EARLIER`),
                        })}
                    </span>
                    <div className={css.previewActions}>
                        <Button
                            type="secondary"
                            onClick={() => handleRestoreVersion(previewVersionMeta)}
                            showSpinner={restoringVersionId === previewingVersionId}
                            disabled={restoringVersionId != null}
                        >
                            {translate(`${PREVIEW_BANNER_PREFIX}.RESTORE_THIS_VERSION`)}
                        </Button>
                        <Button type="flat" onClick={exitPreview}>
                            {translate(`${PREVIEW_BANNER_PREFIX}.EXIT_PREVIEW`)}
                        </Button>
                    </div>
                </div>
            )}

            <div className={css.body}>
                <SectionsSidebar
                    sections={SECTIONS}
                    answeredBySectionId={answeredBySectionId}
                    activeSectionId={activeSectionId}
                    onSectionClick={handleSidebarClick}
                />

                <div className={css.content} onFocus={handleSectionFocus}>
                    <AlertMessage
                        level="info"
                        layout={LAYOUT_OPTIONS.SNACKBAR}
                        title={translate(`${AI_CONTEXT_PREFIX}.CONFIGURE_TITLE`)}
                        text={translate(`${AI_CONTEXT_PREFIX}.CONFIGURE_TEXT`)}
                        className={css.banner}
                    />

                    {isLoading &&
                        SECTIONS.map(section => (
                            <SectionSkeleton key={section.id} questionsCount={section.questions.length} />
                        ))}

                    {!isLoading &&
                        SECTIONS.map(section => (
                            <ContextSection
                                key={section.id}
                                ref={setSectionRef(section.id)}
                                section={section}
                                answers={visibleAnswers}
                                maxLengths={maxLengths}
                                isOpen={openSections[section.id]}
                                onToggle={handleToggleSection}
                                onChange={handleChange}
                                readOnly={isPreviewing}
                            />
                        ))}
                    {!isLoading && <div ref={setSentinelRef} aria-hidden />}
                </div>
            </div>

            <VersionsShelf
                open={isVersionsShelfOpen}
                onClose={handleCloseVersions}
                onPreview={handlePreviewVersion}
                onRestore={handleRestoreVersion}
                currentVersionId={loadedLatestVersionId}
                previewingVersionId={previewingVersionId}
                loadingPreviewVersionId={loadingPreviewVersionId}
                restoringVersionId={restoringVersionId}
            />

            <ConflictDialog
                conflict={conflict}
                onCancel={cancelConflict}
                onResolve={resolveConflict}
                isResolving={isResolvingConflict}
            />
        </PageWrapper>
    );
};

AiContext.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withTranslate(AiContext);
